import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Alert, Spinner, Form, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { useCart } from '../contexts/CartContext';

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [additionalEmail, setAdditionalEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [deliveryStreetAddress, setDeliveryStreetAddress] = useState('');
  const [deliveryPostalCode, setDeliveryPostalCode] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { notify, confirm } = useNotification();
  const { refreshCart } = useCart();

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/cart');
      setCartItems(response.data?.items || []);
      setError(null);
    } catch (err) {
      setError(t('common.error'));
      console.error('Failed to fetch cart:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCheckout = async () => {
    try {
      const profileResponse = await api.get('/api/user/profile');
      const address = profileResponse.data?.address;
      if (address) {
        setDeliveryStreetAddress(address.address_line1 || '');
        setDeliveryPostalCode(address.postal_code || '');
        setDeliveryCity(address.city || '');
      }
    } catch (err) {
      console.error('Failed to load profile address:', err);
    }
    setShowCheckout(true);
  };

  const updateQuantity = async (itemId, newQuantity) => {
    try {
      await api.put(`/api/cart/${itemId}`, { quantity: newQuantity });
      fetchCart();
      refreshCart();
    } catch (err) {
      notify({ variant: 'danger', message: t('common.error') });
      console.error('Failed to update cart:', err);
    }
  };

  const removeItem = async (itemId) => {
    try {
      const confirmed = await confirm({
        title: t('cart.confirmRemoveTitle'),
        message: t('cart.confirmRemoveMessage'),
        confirmText: t('cart.remove')
      });

      if (!confirmed) {
        return;
      }

      await api.delete(`/api/cart/${itemId}`);
      fetchCart();
      refreshCart();
    } catch (err) {
      notify({ variant: 'danger', message: t('common.error') });
      console.error('Failed to remove item:', err);
    }
  };

  const handleCheckout = async () => {
    try {
      const response = await api.post('/api/orders', {
        additionalEmail: additionalEmail || undefined,
        notes: notes || undefined,
        language,
        deliveryStreetAddress,
        deliveryPostalCode,
        deliveryCity,
        deliveryDate
      });
      
      notify({
        variant: 'success',
        title: t('cart.orderCreated'),
        message: `${t('cart.orderNumber')} ${response.data.orderNumber}`
      });
      setShowCheckout(false);
      refreshCart();
      navigate('/orders');
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('common.error')
      });
      console.error('Failed to create order:', err);
    }
  };

  if (loading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4">{t('cart.title')}</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {cartItems.length === 0 ? (
        <Alert variant="info">{t('cart.empty')}</Alert>
      ) : (
        <>
          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>{t('cart.columns.product')}</th>
                <th>{t('cart.quantity')}</th>
                <th>{t('cart.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {cartItems.map((item) => {
                const product = item.product || {};
                const images = Array.isArray(product.images) ? product.images : [];
                const primaryImage = images.find((img) => img?.isPrimary || img?.is_primary) || images[0] || null;
                const imageUrl =
                  primaryImage?.imageUrl ||
                  primaryImage?.image_url ||
                  product.imageUrl ||
                  product.image_url ||
                  null;

                return (
                  <tr key={item.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        {imageUrl && (
                          <img 
                            src={imageUrl} 
                            alt={product.name || ''} 
                            className="cart-item-image me-3"
                          />
                        )}
                        <div>
                          <strong>{product.name}</strong>
                          <br />
                          <small className="text-muted">{product.description}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min="1"
                        max={product.stock ?? 0}
                        value={item.quantity}
                        onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                        style={{ width: '80px' }}
                      />
                    </td>
                    <td>
                      <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => removeItem(item.id)}
                      >
                        {t('cart.remove')}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="text-end">
            <Button 
              variant="primary" 
              size="lg" 
              className="mt-3"
              onClick={() => openCheckout()}
            >
              {t('cart.checkout')}
            </Button>
          </div>
        </>
      )}

      <Modal show={showCheckout} onHide={() => setShowCheckout(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('cart.checkoutTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <h6 className="mb-3">{t('cart.deliveryInformation')}</h6>
            <Form.Group className="mb-3">
              <Form.Label>{t('cart.deliveryStreetAddressLabel')} *</Form.Label>
              <Form.Control
                type="text"
                value={deliveryStreetAddress}
                onChange={(e) => setDeliveryStreetAddress(e.target.value)}
                placeholder={t('cart.deliveryStreetAddressPlaceholder')}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('cart.deliveryPostalCodeLabel')} *</Form.Label>
              <Form.Control
                type="text"
                value={deliveryPostalCode}
                onChange={(e) => setDeliveryPostalCode(e.target.value)}
                placeholder={t('cart.deliveryPostalCodePlaceholder')}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('cart.deliveryCityLabel')} *</Form.Label>
              <Form.Control
                type="text"
                value={deliveryCity}
                onChange={(e) => setDeliveryCity(e.target.value)}
                placeholder={t('cart.deliveryCityPlaceholder')}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('cart.deliveryDateLabel')} *</Form.Label>
              <Form.Control
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                required
              />
            </Form.Group>

            <hr />

            <Form.Group className="mb-3">
              <Form.Label>{t('cart.additionalEmailLabel')}</Form.Label>
              <Form.Control
                type="email"
                value={additionalEmail}
                onChange={(e) => setAdditionalEmail(e.target.value)}
                placeholder={t('cart.additionalEmailPlaceholder')}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('cart.notesLabel')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('cart.notesPlaceholder')}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCheckout(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleCheckout}
            disabled={!deliveryStreetAddress || !deliveryPostalCode || !deliveryCity || !deliveryDate}
          >
            {t('cart.confirmOrder')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Cart;
